import { useTheme } from "@mui/material";
import isHotkey from "is-hotkey";
import React, { useCallback } from "react";
import { BaseEditor, Descendant, Editor, Element as SlateElement, Node, Transforms } from "slate";
import { Editable, ReactEditor, Slate, useSlate } from "slate-react";
import { EditableProps } from "slate-react/dist/components/editable";

import ArrowCircleUpOutlinedIcon from "@/Components/Icons/ArrowCircleUpOutlinedIcon";
import BoldIcon from "@/Components/Icons/BoldIcon";
import ItalicIcon from "@/Components/Icons/ItalicIcon";
import UnderlineIcon from "@/Components/Icons/UnderlineIcon";
import { Box } from "@/Components/v2/atoms/Box";
import { Stack } from "@/Components/v2/atoms/Stack";
import { colors } from "@/Components/v2/utils/brand-colors";

import { RichTextIconButton } from "../../atoms/Button/RichTextIconButton";
import { StyledTextEditorContainer } from "../../atoms/StyledTextEditorContainer/StyledTextEditorContainer";
import Toolbar from "../../atoms/Toolbar/Toolbar";
import { useCharacterLimit } from "./hooks/useCharacterLimit";

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
};

const LIST_TYPES = ["numbered-list", "bulleted-list"];

const TOOLBAR_ITEMS = {
  bold: { format: "bold", icon: <BoldIcon fontSize="small" /> },
  italic: { format: "italic", icon: <ItalicIcon fontSize="small" /> },
  underline: { format: "underline", icon: <UnderlineIcon fontSize="small" /> },
  // "numbered-list": { format: "numbered-list", icon: <ListNumberedIcon fontSize="small" /> },
  // "bulleted-list": { format: "bulleted-list", icon: <ListBulletIcon fontSize="small" /> },
};

type ToolbarElements = keyof typeof TOOLBAR_ITEMS;

interface SlateEditorProps extends EditableProps {
  toolbarElements: ToolbarElements[];
  editor: ReactEditor;
  initialValue?: Descendant[];
  onSubmit: () => void;
  maxCharacters?: number;
}
interface CustomElement extends SlateElement {
  type: string;
}

const DEFAULT_INITIAL_VALUE: Descendant[] = [{ children: [{ text: "" }] }];
const DEFAULT_MAX_CHARACTERS = 1000;

const SlateEditor: React.FC<SlateEditorProps> = ({
  toolbarElements = [],
  editor,
  initialValue = DEFAULT_INITIAL_VALUE,
  onSubmit,
  maxCharacters = DEFAULT_MAX_CHARACTERS,
  ...props
}) => {
  const renderElement = useCallback((props: any) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);

  const { isLimitReached, checkAndPreventInput } = useCharacterLimit({
    editor,
    maxCharacters,
  });

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const text = event.dataTransfer.getData("text");

      if (checkAndPreventInput(text.length, event)) {
        const range = ReactEditor.findEventRange(editor, event);
        Transforms.select(editor, range);
        editor.insertText(text);
      }
    },
    [editor, checkAndPreventInput]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      const text = event.clipboardData.getData("text");
      checkAndPreventInput(text.length, event);
    },
    [checkAndPreventInput]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const isTextInput =
        event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

      if (isTextInput) {
        checkAndPreventInput(1, event);
      }

      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event as any)) {
          event.preventDefault();
          const mark = HOTKEYS[hotkey as keyof typeof HOTKEYS];
          toggleMark(editor, mark);
        }
      }
    },
    [editor, checkAndPreventInput]
  );

  return (
    <Slate editor={editor} initialValue={initialValue}>
      <Box
        sx={{ border: `1px solid ${colors.dropdownBorder}`, borderRadius: 2, position: "relative" }}
      >
        <StyledTextEditorContainer sx={{ borderBottom: `1px solid ${colors.dropdownBorder}` }}>
          <Editable
            style={{
              padding: 10,
              fontSize: 14,
            }}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            spellCheck
            autoFocus
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            {...props}
          />
          {isLimitReached && (
            <Stack direction="row" justifyContent="end" width="100%">
              <Box padding={1} sx={{ color: "red" }}>
                Character limit reached
              </Box>
            </Stack>
          )}
        </StyledTextEditorContainer>
        <Toolbar sx={{ padding: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
            <Stack spacing={1} direction="row">
              {toolbarElements.map((element) => {
                const item = TOOLBAR_ITEMS[element as keyof typeof TOOLBAR_ITEMS];
                if (!item) return null;
                const ButtonComponent = LIST_TYPES.includes(item.format) ? BlockButton : MarkButton;
                return <ButtonComponent key={element} format={item.format} icon={item.icon} />;
              })}
            </Stack>
            <Stack direction="row" justifyContent="center" alignItems="center">
              <RichTextIconButton size="small" sx={{ padding: 2 }} onClick={onSubmit}>
                <ArrowCircleUpOutlinedIcon fontSize="medium" />
              </RichTextIconButton>
            </Stack>
          </Stack>
        </Toolbar>
      </Box>
    </Slate>
  );
};

const toggleBlock = (editor: BaseEditor, format: string) => {
  const isActive = isBlockActive(editor, format, "type");
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n: Node) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes((n as CustomElement).type),
    split: true,
  });
  const newProperties: Partial<CustomElement> = {
    type: isActive ? "paragraph" : isList ? "list-item" : format,
  };
  Transforms.setNodes<SlateElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const toggleMark = (editor: BaseEditor, format: string) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor: BaseEditor, format: string, blockType = "type") => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes<CustomElement>(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n: Node) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n as CustomElement)[blockType as keyof CustomElement] === format,
    })
  );

  return !!match;
};

const isMarkActive = (editor: Editor, format: string) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format as keyof typeof marks] === true : false;
};

const Element = ({
  attributes,
  children,
  element,
}: {
  attributes: any;
  children: any;
  element: any;
}) => {
  const style = { textAlign: element.align };
  switch (element.type) {
    case "bulleted-list":
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case "list-item":
      return (
        <li style={{ ...style, listStyleType: "unset" }} {...attributes}>
          {children}
        </li>
      );
    case "numbered-list":
      return (
        <ol style={{ ...style, listStyleType: "decimal" }} {...attributes}>
          {children}
        </ol>
      );
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children, leaf }: { attributes: any; children: any; leaf: any }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};

const BlockButton = ({ format, icon }: { format: string; icon: React.ReactNode }) => {
  const editor = useSlate();
  const theme = useTheme();
  const isActive = isBlockActive(editor, format, "type");
  return (
    <RichTextIconButton
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
      sx={{
        padding: 2,
        backgroundColor: isActive ? theme.palette.datePicker.selectedRange : "transparent",
      }}
    >
      {icon}
    </RichTextIconButton>
  );
};

const MarkButton = ({ format, icon }: { format: string; icon: React.ReactNode }) => {
  const editor = useSlate();
  const theme = useTheme();
  const isActive = isMarkActive(editor, format);
  return (
    <RichTextIconButton
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
      sx={{
        padding: 2,
        backgroundColor: isActive ? theme.palette.datePicker.selectedRange : "transparent",
      }}
    >
      {icon}
    </RichTextIconButton>
  );
};

export default SlateEditor;
